import { injectable, inject, multiInject } from 'inversify';
import Debug from 'debug'
import * as Api from '@xlearn/common';
import { CancelToken, ResettableCancelToken } from '@/app/shared/utils/cancel-token';
import { CourseService, CourseServiceSymbol } from '@/app/aspect/course-service';
import {
  SectionProgressInterface,
  SectionProgressSymbol,
  SectionCompletion
} from '@/app/statistics/section/section-progress';
import {
  CourseAspect,
  TocItem,
  File,
  Term,
  AspectSelectInfo
} from '@/app/shared/models';
import { changeFavicon } from '@/app/shared/favicon'
import { TrackingService } from '@/app/tracking/tracking-service';
import { allSectionIdsUnderToc, tocHrefEqualsPage } from '@/app/aspect/toc-utils';
import { walkObjectAndReplaceBasePaths } from '@/app/shared/utils/base-path';
import aspectStoreModule from '@/app/aspect/apsect-state';
import { PlayerModule, PlayerModuleSymbol } from '../api'
import { reportNonFatalError } from '../error-handling';
import { StartCourseCommand } from '../course/tracking/api';
import * as TrackingApi from '../tracking/api';
import { SerializedSearchIndex, SerializedCompetence, SerializedCourseDetails } from '@xlearn/common';
import { MsgPackCourseFileLoader, MsgPackCourseFileLoaderSymbol } from '../fetch/msgpack-course-file';
import { DEFAULT_LOCALE } from '@/locales/default-locale';
import { changeLocale } from '@/app/locale/change-locale';
import * as StudentInfoTrackingApi from '@/app/student/tracking/api'

const debug = Debug('aspect-service')

const MVM_ASPECT_HACK = true
const MVM_ASPECT_MAP =
      // [{ text: '\u200B\u200B\u200B', aspectInstanceId: 'le' },
      //  { text: '\u200B\u200B\u200C', aspectInstanceId: 'lk' }]
      [
        { texts: [ 'MVM Zrt.', '\u200B\u200B\u200B' ], aspectInstanceId: 'mvm' },
        { texts: [ 'MVM Services Zrt.', '\u200B\u200B\u200C' ], aspectInstanceId: 'mvms' },
        { texts: ['MVM Démász Áramhálózati Kft.', '\u200B\u200C\u200B' ], aspectInstanceId: 'dem' }
      ]

@injectable()
export class AspectService {

  private cancelToken: ResettableCancelToken
                            
  constructor(
    @inject(TrackingService)
    private trackingService: TrackingService,
    @inject(CourseServiceSymbol)
    private courseService: CourseService,
    @inject(SectionProgressSymbol)
    private sectionProgressService: SectionProgressInterface,
    @inject(MsgPackCourseFileLoaderSymbol)
    private fileLoader: MsgPackCourseFileLoader,
    @multiInject(PlayerModuleSymbol)
    private playerModules: PlayerModule[]) {

    this.cancelToken = new ResettableCancelToken()
  }

  public init() {
    debug('init', this.playerModules.map(x=>x.name))
    for (let mod of this.playerModules) {
      if (mod.init) {
        mod.init()
      }
    }
  }

  public async loadAspectsToStore(): Promise<void> {
    debug('loadAspectsToStore')

    const course = await this.queryCourseAndSetToStore();

    const orderedAspects = course.aspects.sort((a, b) => a.order < b.order ? -1 : 1);

    let aspects: AspectSelectInfo[] = [];
    for (let i = 0; i < orderedAspects.length; i++) {
      const aspect = orderedAspects[i];
      aspects = [...aspects, {
        level: aspect.order,
        name: aspect.name,
        values: (aspect as any).values
      }];
    }

    aspectStoreModule.setAspects(aspects);
  }

  private async determineDefaultAspectInstanceId(courseId: string) {

    if (MVM_ASPECT_HACK) {
      // determine aspect instance id based on "hidden" string in the student's name
      debug('determine aspect based on hidden string in the name')
      const aspectInstanceId = await this.determineMvmAspectInstanceId()
      debug('found aspect', aspectInstanceId)

      if (aspectInstanceId) {
        return aspectInstanceId
      }
    }

    debug('load saved aspect')
    const aspectInstanceId = await this.getSavedAspectId(courseId)
    debug('loaded saved aspect id', aspectInstanceId)
    return aspectInstanceId
  }

  private async getSavedAspectId(courseId: string) {
    // HACK: store selected aspect now in local storage
    const key = courseId + '#aspect';
    const aspectInstanceId = localStorage.getItem(key)
    debug('loaded selected aspect id from local storage', key, aspectInstanceId)
    return aspectInstanceId
  }

  private async determineMvmAspectInstanceId() {
    const studentInfo = await this.queryStudentInfo()

    for (const mapItem of MVM_ASPECT_MAP) {
      debug(`checking aspect '${mapItem.aspectInstanceId}'...`)

      for (const text of mapItem.texts) {
        debug(`checking '${text}' in '${studentInfo.fullName}'`)
        if (studentInfo.fullName.indexOf(text) >= 0) {
          debug('matched aspect instance id', mapItem.aspectInstanceId)
          return mapItem.aspectInstanceId
        }
      }
    }

    return null
  }

  private async queryStudentInfo() {
    debug('queryStudentInfo')

    const query = TrackingApi.createQuery<StudentInfoTrackingApi.QueryStudentInfoTrackingQuery>('query-student-info', {})
    const result = await this.trackingService.query(query)

    debug('studentInfo', result.studentInfo)

    return result.studentInfo
  }

  // TODO: refactor aspect change, stop services, etc...
  public async set(aspectInstanceId: string|null = null, isFirstStart: boolean): Promise<void> {

    debug('set', aspectInstanceId, isFirstStart)

    const course = await this.queryCourseAndSetToStore();

    if (isFirstStart) {
      debug('starting tracking service...')
      await this.startTrackingService(course.id);
      debug('tracking service started')
    }

    if (!aspectInstanceId) {
      aspectInstanceId = await this.determineDefaultAspectInstanceId(course.id)
      const isValidAspect = aspectInstanceId && course.aspectInstances.find((i) => i.id === aspectInstanceId)
      if (isValidAspect) {
        // no need to select aspect
        debug('use determined aspect', aspectInstanceId)
        aspectStoreModule.disableStartupAspectSelection()
      } else {
        // use the default
        debug('setting default aspect', course.defaultAspectInstanceId)
        aspectInstanceId = course.defaultAspectInstanceId
        aspectStoreModule.enableStartupAspectSelection()
      }
    }

    if (!aspectInstanceId) {
      throw new Error('Default aspect instance not found: ' + aspectInstanceId);
    }

    if (!isFirstStart) {
      // HACK: for aspect selector
      const key = course.id + '#aspect';
      debug('saving selected aspect id in local storage', key, aspectInstanceId)
      localStorage.setItem(key, aspectInstanceId)
    }

    let courseAspect: CourseAspect|null = await this.findCourseAspect(
      aspectInstanceId,
      course.aspectInstances,
      course.id
    );

    if (courseAspect !== null) {
      courseAspect = walkObjectAndReplaceBasePaths(courseAspect, courseAspect.basePath) as CourseAspect;

      const flatPages: string[] = this.getFlatPages(courseAspect.tocItems);
      aspectStoreModule.setFlatPages(flatPages);

      const sectionsById = this.getSectionsById(courseAspect.tocItems);
      aspectStoreModule.setSectionsById(sectionsById);

      const language = courseAspect.language
      changeLocale(language)

      // set aspect icon, TODO: place somewhere else?
      if (courseAspect.iconUri) {
        debug('changing favicon to aspect\'s one', courseAspect.iconUri);
        changeFavicon(courseAspect.iconUri)
      } else {
        debug('aspect has no favicon, skipping', courseAspect.basePath);
      }
    }

    aspectStoreModule.setSelectedCourseAspect(courseAspect);

    const instance = course.aspectInstances.find((i) => i.id === aspectInstanceId);
    if (!instance) { throw new Error('Coding error: Instance not found with id: ' + aspectInstanceId); }

    for (let playerModule of this.playerModules) {
      if (isFirstStart) {
        debug('starting module', playerModule.name)
        playerModule.start(instance)
      } else {
        debug('resetting module', playerModule.name)
        playerModule.reset(instance)
      }
    }

    if (courseAspect !== null) {
      courseAspect.tocItems = await this.setChapterPercents(courseAspect.tocItems);
      aspectStoreModule.setSelectedCourseAspect(courseAspect);
    }

    aspectStoreModule.setSelectedAspectInstanceId(aspectInstanceId);
  }

  public getFirstPageId(tocItems: TocItem[]|null = null): string|null {
    if (aspectStoreModule.selectedCourseAspectInstance === null) {
      return null;
    }

    if (tocItems === null) {
      tocItems = aspectStoreModule.selectedCourseAspectInstance.tocItems as TocItem[];
    }

    for (const tocItem of tocItems) {
      if (tocItem.href !== null) {
        return tocItem.href;
      }

      if (tocItem.children) {
        const foundInChildren: string|null = this.getFirstPageId(tocItem.children);
        if (foundInChildren !== null) {
          return foundInChildren;
        }
      }
    }

    return null;
  }

  public async calculateSelectedChapter(sectionId: string, forceRecalculatePercents: boolean = false): Promise<void> {
    let selectedChapter: TocItem|null = this.getSelectedChapter(sectionId);

    if (!forceRecalculatePercents) {
      const alreadyInStore: boolean = selectedChapter !== null
        && aspectStoreModule.selectedChapter !== null
        && selectedChapter.id === aspectStoreModule.selectedChapter.id;
      if (alreadyInStore) {
        return;
      }
    }

    if (selectedChapter !== null) {
      selectedChapter = await this.addPageCompletionPercentsToChapter(selectedChapter);
    }

    aspectStoreModule.setSelectedChapter(selectedChapter);
  }

  public calculateSectionByPage(page: string, tocItems: TocItem[]|null = null): TocItem|null {
    if (tocItems === null) {
      if (aspectStoreModule.selectedCourseAspectInstance === null) {
        return null;
      }

      tocItems = aspectStoreModule.selectedCourseAspectInstance.tocItems;
    }

    for (const item of tocItems) {
      if (item.href && tocHrefEqualsPage(page, item.href)) {
        return item;
      }

      if (item.children) {
        const found = this.calculateSectionByPage(page, item.children);
        if (found !== null) {
          return found;
        }
      }
    }

    return null;
  }

  private async startTrackingService(courseId: string) {
    const result = await this.trackingService.start({
        courseId,
        // aspect: {
        //   aspectId: aspectInstanceId, // TODO?
        //   aspectInstanceId
        // }
    });

    if (!result.success) {
      if (result.error.code != 'already-started') {
        reportNonFatalError('Error occurred while initializing tracking', JSON.stringify(result.error))
        alert(`Nem sikerült kapcsolódni az oktatási keretrendszerhez, kérjük, indítsa újra a tananyagot.\n\nRészletek: ${result.error.code}; ${result.error.details}`)
      }
    }

    console.debug('Tracking engine result', result);
    if (result.success) {
      this.trackingService.execute(TrackingApi.createCommand<StartCourseCommand>('start-course', {}))
    }
  }

  private async queryCourseAndSetToStore() {
    let course = aspectStoreModule.course;
    if (aspectStoreModule.course === null) {
      const result = await this.courseService.getCourse();
      if (!result.success) {
        throw new Error('Could not load the course: ' + JSON.stringify(result.error))
      }

      course = result.value
      aspectStoreModule.setCourse(course);
    }

    if (course === null) {
      throw new Error('Could not load course');
    }

    return course;
  }

  private getSelectedChapter(
    sectionId: string,
    currentChapter: TocItem|null = null,
    tocItems: TocItem[]|null = null
  ): TocItem|null {
    if (aspectStoreModule.selectedCourseAspectInstance === null) {
      return null;
    }

    let isChapterItem: boolean = false;
    if (tocItems === null) {
      isChapterItem = true;
      tocItems = aspectStoreModule.selectedCourseAspectInstance.tocItems;
    }

    for (const tocItem of tocItems) {
      if (isChapterItem) {
        currentChapter = tocItem;
      }

      if (tocItem.id === sectionId) {
        return currentChapter;
      }

      if (tocItem.children) {
        const foundInChildren: TocItem|null = this.getSelectedChapter(sectionId, currentChapter, tocItem.children);
        if (foundInChildren !== null) {
          return foundInChildren;
        }
      }
    }

    return null;
  }

  private async addPageCompletionPercentsToChapter(chapter: TocItem): Promise<TocItem> {
    const result = await this.setPagePercents(chapter);
    chapter.percent = Math.round(result.chapterAchievedPercent / result.pageCount);
    return chapter;
  }

  private async setPagePercents(
    tocItem: TocItem,
    chapterAchievedPercent: number = 0,
    pageCount: number = 0
  ): Promise<{ chapterAchievedPercent: number; pageCount: number; }> {
    if (!tocItem.children || tocItem.children.length === 0) {
      return { chapterAchievedPercent, pageCount };
    }

    let result: { chapterAchievedPercent: number; pageCount: number; } = { chapterAchievedPercent, pageCount };
    for (const child of tocItem.children) {
      if (child.href !== null) {
        result.pageCount++;

        const completion: SectionCompletion|null = this.sectionProgressService.find(child.id);
        if (completion !== null) {
          const percent = completion.progress.completion;
          child.percent = isNaN(percent) ? 0 : percent;
          result.chapterAchievedPercent += child.percent;
        }
      }

      result = await this.setPagePercents(child, result.chapterAchievedPercent, result.pageCount);
    }

    return result;
  }

  private async findCourseAspect(
    aspectInstanceId: string,
    aspectInstances: Api.SerializedAspectInstance[],
    courseId: string
  ): Promise<CourseAspect|null> {

    debug('findCourseAspect', aspectInstanceId, courseId)

    const instance = aspectInstances.find((i) => i.id === aspectInstanceId);
    if (!instance) {
      return null;
    }

    const details = instance.details;
    const basePath: string = instance.basePath;
    const files = walkObjectAndReplaceBasePaths(details.files, basePath);

    const token = this.cancelToken.cancelAndReset()
    const fetchResults = await this.fetchAll(files, token)
    
    const tocItems: TocItem[] = fetchResults.toc
    let tocItemsWithPercents: TocItem[] = [];
    for (const tocItem of tocItems) {
      tocItemsWithPercents = [...tocItemsWithPercents, await this.addPageCompletionPercentsToChapter(tocItem)];
    }

    // TODO: use per domain file loading
    const result: CourseAspect = {
      courseId,
      language: details.language || DEFAULT_LOCALE,
      title: details.courseTitle,
      subtitle: details.courseSubtitle || '',
      description: details.courseDescription,
      iconUri: details.courseIconUri || null,
      imageUri: details.courseImageUri || '',
      tocItems: tocItemsWithPercents,
      // questions: this.mapQuestions(
      //   await this.fetchAndDecodeFile<Api.SerializedQuestion[]>(files.questionBankUri, token, [])),
      // quizzes: this.mapQuizzes(
      //   await this.fetchAndDecodeFile<Api.SerializedQuiz[]>(files.quizBankUri, token, [])),
//      finalQuizId: details.finalQuizId,
      basePath,
      files: fetchResults.files,
      terms: fetchResults.terms,
      search: fetchResults.search,
      competences: fetchResults.competences,
      sectionImageUri: details.sectionImageUri ? details.sectionImageUri : null,
      sectionBackgroundUri: details.sectionBackgroundUri ? details.sectionBackgroundUri : null,
      pdfUri: files.pdfUri !== '' ? files.pdfUri : null,
      time: details.time ? details.time : null,
      revisionDate: details.revisionDate ? details.revisionDate : null,
      revisionNumber: details.revisionNumber ? details.revisionNumber : null, 
      authors: details.authors ? details.authors : [],
      preamble: details.preambleUri ? details.preambleUri : null,
    };

    debug('findCourseAspect result', result)

    return result
  }

  // REVIEW: no these are preloaded unnecessarily, it should be loaded in domain dependent manner
  private async fetchAll(files: SerializedCourseDetails['files'], token: CancelToken) {

    let result: {
      toc?: TocItem[],
      files?: File[],
      terms?: Term[],
      search?: SerializedSearchIndex,
      competences?: SerializedCompetence[]
    } = {}

    const fetchTasks = [
      (async () => {
        result.toc = this.mapTocItems(
          await this.fetchAndDecodeFile<Api.SerializedTocItem[]>(files.tocUri, token, []))
      })(),
      (async () => {
        result.files = this.mapFiles(
          await this.fetchAndDecodeFile<Api.SerializedFile[]>(files.fileBankUri, token, []))
      })(),
      (async () => {
        result.terms = this.mapTerms(
          await this.fetchAndDecodeFile(files.termBankUri, token, []));
      })(),
      (async () => {
        result.search =
        await this.fetchAndDecodeFile<SerializedSearchIndex>(files.searchIndexUri, token, {
          lunrIndex: {},
          entries: []
        })
      })(),
      (async () => {
        result.competences =
        await this.fetchAndDecodeFile<SerializedCompetence[]>(files.competencesUri, token, [])
      })(),
    ]

    // wait all
    await Promise.all(fetchTasks)

    return {
      toc: result.toc!,
      files: result.files!,
      terms: result.terms!,
      search: result.search!,
      competences: result.competences!
    }
  }

 // TODO: remove all domain specific code from the aspect service
  private async fetchAndDecodeFile<T>(uri: string, cancelToken: CancelToken, defaultIfError: T) {

    debug('fetchAndDecodeFile', uri)

    const loadResult = await this.fileLoader.load<T>(uri, cancelToken)
    if (!loadResult.success) {
      debug('fetchAndDecodeFile error', uri, loadResult.error)
      return defaultIfError
    }

    debug('fetchAndDecodeFile result', uri, loadResult.value)

    return loadResult.value
  }

  private mapTocItems(items: Api.SerializedTocItem[]): TocItem[] {
    return items.map((x) => ({
      ...x,
      href: x.href || null,
      children: x.children ? this.mapTocItems(x.children) : undefined
    }));
  }

  // private mapQuizzes(quizzes: Api.SerializedQuiz[]): Quiz[] {
  //   // TODO: remove "as Quiz" and change type of the local type or create appropriate mapping
  //   return quizzes.map((x) => x as unknown as Quiz);
  // }

  // private mapQuestions(questions: Api.SerializedQuestion[]): Question[] {
  //   return questions.map((x) => ({
  //     ...x,
  //     content: x.content as QuestionContent,
  //     type: mapQuestionType(x.type)
  //   }));

  //   function mapQuestionType(type: string): QuestionType {
  //     switch (type) {
  //       case 'choice':
  //         return QuestionType.choice;
  //       case 'sorting':
  //         return QuestionType.sorting;
  //       case 'grouping':
  //         return QuestionType.grouping;
  //       case 'matching':
  //         return QuestionType.matching;
  //       case 'short-answer':
  //         return QuestionType.shortAnswer;
  //       case 'scenario':
  //         return QuestionType.scenario;
  //       case 'fill-in':
  //         return QuestionType.fillIn;
  //       case 'hotspot':
  //         return QuestionType.hotspot;
  //       default:
  //         return QuestionType.unknown;
  //     }
  //   }
  // }

  private mapFiles(files: Api.SerializedFile[]): File[] {
    return files.map((f) => {
      return {
        title: f.title,
        originalFilename: f.originalFilename,
        mimeType: f.mimeType,
        description: f.description,
        size: f.sizeHumanReadable,
        uri: f.uri,
        topicIds: f.topicIds
      };
    });
  }

  private mapTerms(terms: Api.SerializedTerm[]): Term[] {
    return terms.map((t) => t);
  }


  private getFlatPages(tocItems: TocItem[]): string[] {
    let results: string[] = [];
    for (const tocItem of tocItems) {

      if (tocItem.href) {
        results = [...results, tocItem.href];
      }

      if (tocItem.children) {
        results = [
          ...results,
          ...this.getFlatPages(tocItem.children)
        ];
      }
    }

    // remove duplicates in a row
    let cleaned: string[] = [];
    for (const result of results) {
      const prevItem: string | null = cleaned.length > 0 ? cleaned[cleaned.length - 1] : null;
      if (prevItem !== null) {
        const prevPage: string = prevItem.split('#')[0];
        const currentPage: string = result.split('#')[0];
        const isSamePage: boolean = prevPage === currentPage;
        if (isSamePage) {
          continue;
        }
      }

      if (result === prevItem) {
        continue;
      }

      cleaned = [...cleaned, result];
    }

    return cleaned;
  }

  private getSectionsById(tocItems: TocItem[]): { [sectionId: string]: TocItem } {
    let results: { [sectionId: string]: TocItem } = {};
    for (const tocItem of tocItems) {
      results[tocItem.id] = tocItem;

      if (tocItem.children) {
        results = {
          ...results,
          ...this.getSectionsById(tocItem.children)
        };
      }
    }

    return results;
  }

  private async setChapterPercents(tocItems: TocItem[]): Promise<TocItem[]> {
    let sectionIds: string[] = [];
    for (const chapter of tocItems) {
      sectionIds = [...sectionIds, ...allSectionIdsUnderToc(chapter)];
    }

    await this.sectionProgressService.load(sectionIds);

    let chapters: TocItem[] = [];
    for (const chapter2 of tocItems) {
      const chapterWithPercent: TocItem = await this.addPageCompletionPercentsToChapter(chapter2);
      chapters = [...chapters, chapterWithPercent];
    }

    return chapters;
  }
}

export const AspectServiceSymbol = Symbol.for('AspectService');
